import { SearchOutlined, DownOutlined } from "@ant-design/icons";

import { useState, useEffect, useCallback } from "react";

import PropTypes from "prop-types";

import { Input, Typography, Spin, Button } from "antd";

import axios from "axios";
import { useToast } from "../../../../contexts/ToastContext";

const { Title } = Typography;

const SelectCarModel = ({
    selectedCarModel,
    selectedCompany,
    setSelectedCarModel,
    setStep,
    t, // Translation function
}) => {
    const toast = useToast();
    const [searchModel, setSearchModel] = useState("");
    const [carModels, setCarModels] = useState([]);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const fetchCarModels = useCallback(
        async (companyName, currentOffset, searchModelText) => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/all-vehicles-model/records?select=model%2C%20make%2C%20id
                &${
                    searchModelText.trim() ? `where=search(model,"${searchModelText}")&` : ""
                }order_by=year%20DESC&limit=100&offset=${currentOffset}&refine=make%3A%22${companyName}%22`,
                );

                if (currentOffset === 0) {
                    // Filter unique models
                    const uniqueModels = new Set();
                    const results = response?.data?.results
                        ?.filter((model) => {
                            if (!uniqueModels.has(model.model)) {
                                uniqueModels.add(model.model);
                                return true;
                            }
                            return false;
                        })
                        .map((model) => ({
                            id: model.id,
                            model: model.model,
                            make: model.make,
                        }));
                    setCarModels(results || []);
                } else {
                    // Use functional update to avoid dependency on carModels
                    setCarModels((prevCarModels) => {
                        // Get existing model names - convert to lowercase for case-insensitive comparison
                        const existingModels = new Set(prevCarModels.map((item) => item.model.toLowerCase()));

                        // Filter out models that already exist (case-insensitive)
                        const newModels = [];
                        response.data.results.forEach((model) => {
                            if (!existingModels.has(model.model.toLowerCase())) {
                                existingModels.add(model.model.toLowerCase());
                                newModels.push({
                                    id: model.id,
                                    model: model.model,
                                    make: model.make,
                                });
                            }
                        });
                        return [...prevCarModels, ...newModels];
                    });
                }
                setOffset(currentOffset + 100);
                setHasMore(response?.data?.results?.length === 100);
            } catch (_error) {
                toast.error({ message: t("failedToFetchModels") });
            } finally {
                setLoading(false);
            }
        },
        [toast, t],
    );

    useEffect(() => {
        if (selectedCompany) {
            fetchCarModels(selectedCompany, 0, "");
        }
    }, [selectedCompany, fetchCarModels]);

    const handleFilterModels = (searchModelText) => {
        fetchCarModels(selectedCompany, 0, searchModelText);
    };

    const handleLoadMore = async () => {
        if (selectedCompany) {
            await fetchCarModels(selectedCompany, offset, searchModel);
        }
    };

    const handleSelectModel = (model) => {
        setSelectedCarModel({ ...model, year: null });
        setStep((prev) => prev + 1);
    };

    return (
        <div className="p-8 max-h-[80vh] overflow-y-auto">
            <Title level={3} className="mb-6 text-center">
                {t("selectModelFor")} {selectedCompany}
            </Title>
            <Input
                size="large"
                prefix={<SearchOutlined className="text-gray-400" />}
                placeholder={t("searchModelPlaceholder")}
                value={searchModel}
                onChange={(e) => {
                    handleFilterModels(e.target.value);
                    setSearchModel(e.target.value);
                }}
                className="mb-6 rounded-lg bg-gray-50 border border-gray-200"
            />
            <div className="text-gray-500 text-sm mb-4">{t("selectYourModel")}</div>
            {loading && carModels.length === 0 ? (
                <div className="flex justify-center items-center h-full w-full">
                    <Spin size="large" />
                </div>
            ) : (
                <div className="max-h-[50vh] overflow-y-scroll">
                    <div className="mb-4 grid grid-cols-3 gap-4">
                        {carModels.map((carModel) => (
                            <button
                                key={carModel.id}
                                type="button"
                                className={`flex items-center gap-2 rounded-lg font-medium text-[15px] transition-all whitespace-normal text-left overflow-hidden ${
                                    selectedCarModel?.model === carModel?.model ? "bg-blue-100 !font-bold" : ""
                                }`}
                                style={{
                                    padding: "8px 12px",
                                    lineHeight: "1.2",
                                }}
                                onClick={() => handleSelectModel(carModel)}>
                                <span className="truncate">{carModel.model}</span>
                            </button>
                        ))}
                    </div>
                    {hasMore && selectedCompany && (
                        <div className="flex justify-center mt-6">
                            <button
                                type="button"
                                className="bg-gray-100 border border-gray-200 rounded-full font-medium text-[15px] flex items-center px-3 py-2"
                                onClick={handleLoadMore}
                                disabled={loading}>
                                {loading ? (
                                    <span>
                                        <Spin size="small" /> {t("loadMore")}
                                    </span>
                                ) : (
                                    t("loadMore", { count: 100 })
                                )}{" "}
                                {!loading && <DownOutlined className="ml-2" size={10} />}
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="flex justify-end gap-4 mt-8">
                <Button onClick={() => setStep((prev) => prev - 1)} className="rounded-full">
                    {t("back")}
                </Button>
            </div>
        </div>
    );
};

SelectCarModel.propTypes = {
    selectedCompany: PropTypes.string.isRequired,
    selectedCarModel: PropTypes.object,
    setSelectedCarModel: PropTypes.func.isRequired,
    setStep: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

export default SelectCarModel;
